import { Box, CircularProgress, ThemeProvider, Typography } from "@mui/material";
import type { Root } from "react-dom/client";
import { UserAuthFields } from "./authProvider";
import { PLATFORM_THEME } from "./constants/themes/platformTheme";
import { queryClient } from "./queryClient";
import type { ICurrentUser } from "./types/users";
import { bootstrapEmbedSession, isEmbedAuthRequested } from "./utils/embedAuth";

const EmbedAuthStatus = ({ failed = false }: { failed?: boolean }) => (
  <ThemeProvider theme={PLATFORM_THEME}>
    <Box role={failed ? "alert" : "status"} sx={{ p: 6, textAlign: "center" }}>
      {failed ? (
        <>
          <Typography variant="h4" component="h1">Unable to sign in</Typography>
          <Typography>Reopen Insights from your portal to try again. If the problem continues, contact your administrator.</Typography>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography>Signing you in…</Typography>
        </>
      )}
    </Box>
  </ThemeProvider>
);

// Runs once at the entry point, outside React mounts/effects: never replay a single-use code.
export const bootstrapApplication = async (
  root: Root,
  renderApp: (initialUser?: ICurrentUser) => void,
): Promise<void> => {
  if (!isEmbedAuthRequested()) {
    renderApp();
    return;
  }

  root.render(<EmbedAuthStatus />);
  try {
    const result = await bootstrapEmbedSession();
    if (result.status === "authenticated") {
      queryClient.clear();
      localStorage.setItem(UserAuthFields.USER_ID, result.user.uid);
      localStorage.setItem(UserAuthFields.USER_NAME, result.user.name);
      renderApp(result.user);
    }
  } catch {
    root.render(<EmbedAuthStatus failed />);
  }
};
